import {
  ChakraBox,
  ChakraFlex,
  ChakraGrid,
  ChakraText,
  Text,
} from '@equip.health/ui';

import {
  CARD_CONTENT_MIN_HEIGHT,
  CARD_STYLES,
  DASHBOARD_TABS,
  EMPTY_STATE_COPY,
  PATIENT_VIEWS,
  PatientViewKey,
} from '../constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { PatientProviderDashboard } from '~/store/services/providerDashboard';
import DashboardCardEmptyState from '../DashboardCardEmptyState';
import DashboardCardErrorState from '../DashboardCardErrorState';
import MyPatientsSkeleton from './MyPatientsSkeleton';
import PatientList from './PatientList';
import PatientRow from './PatientRow';
import DischargePatientRow from './DischargePatientRow';
import TodaysSessionsList from './TodaysSessionsList';
import { useTodaysSessionActions } from './useTodaysSessionActions';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ProviderDashboardEvents } from '~/analytics/providerDashboardEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import usePersistedPatientFilter from './usePersistedPatientFilter';

interface PatientTabGridProps {
  activeTab: PatientViewKey;
  onTabClick: (tabKey: PatientViewKey) => void;
  getTabCount: (tabKey: PatientViewKey) => string | number;
}

const PatientTabGrid = ({
  activeTab,
  onTabClick,
  getTabCount,
}: PatientTabGridProps) => (
  <ChakraGrid
    templateColumns="repeat(4, minmax(0, 1fr))"
    autoRows="auto"
    gap="8px"
    marginBottom="22px"
  >
    {DASHBOARD_TABS.map((tab) => {
      const isActive = activeTab === tab.key;
      return (
        <button
          key={tab.key}
          type="button"
          className={`provider-dashboard__patient-tab${isActive ? ' provider-dashboard__patient-tab--active' : ''}`}
          data-testid={`dashboard-tab-${tab.key}`}
          aria-pressed={isActive}
          onClick={() => onTabClick(tab.key)}
        >
          <ChakraText
            as="span"
            fontSize="19px"
            fontWeight="500"
            lineHeight="1.3"
            color={isActive ? 'accent.blue' : 'neutral.primary'}
            sx={{ fontVariantNumeric: 'tabular-nums' }}
            data-testid={`dashboard-tab-badge-${tab.key}`}
          >
            {getTabCount(tab.key)}
          </ChakraText>
          <ChakraText
            as="span"
            fontSize="13px"
            fontWeight={isActive ? '500' : '400'}
            letterSpacing="-0.39px"
            lineHeight="1.4"
            color={isActive ? 'accent.blue' : 'neutral.primary'}
            whiteSpace="pre-line"
          >
            {tab.label}
          </ChakraText>
        </button>
      );
    })}
  </ChakraGrid>
);

export interface PatientTabData {
  patients: PatientProviderDashboard[];
  isLoading: boolean;
  isError: boolean;
}

interface ProviderDashboardMyPatientsCardProps {
  patientsByTab: Partial<Record<PatientViewKey, PatientTabData>>;
  onReload: () => void;
  isReloading?: boolean;
}

const ProviderDashboardMyPatientsCard = ({
  patientsByTab,
  onReload,
  isReloading = false,
}: ProviderDashboardMyPatientsCardProps) => {
  const { currentUser } = useGlobalContext();
  const [activeTab, setActiveTab] = usePersistedPatientFilter(
    currentUser.userId,
  );

  const { trackEvent } = useAnalytics() ?? {};

  const currentTabData = patientsByTab[activeTab];
  const patients = currentTabData?.patients ?? [];
  const isLoading = currentTabData?.isLoading ?? false;
  const isError = currentTabData?.isError ?? false;
  const isTodayTab = activeTab === PATIENT_VIEWS.today;

  const { canJoin, onJoin, onOpenNote } = useTodaysSessionActions(isTodayTab);

  const tableName = `provider-dashboard-${activeTab}`;

  const loadedTabs = Object.values(patientsByTab).filter(
    (tab) => tab && !tab.isLoading && !tab.isError,
  );
  const allTabsEmpty =
    loadedTabs.length > 0 &&
    loadedTabs.every((tab) => tab!.patients.length === 0);

  const getTabCount = (tabKey: PatientViewKey): string | number => {
    const tabData = patientsByTab[tabKey];
    if (!tabData || tabData.isError || tabData.isLoading) return '—';
    return tabData.patients.length;
  };

  const handleTabClick = (tabKey: PatientViewKey) => {
    if (tabKey !== activeTab) {
      const tabData = patientsByTab[tabKey];
      trackEvent?.(ProviderDashboardEvents.myPatientsTabSwitched, {
        tabKey,
        patientCount: tabData?.patients.length ?? 0,
        Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
      });
      setActiveTab(tabKey);
    }
  };

  const getEmptyStateConfig = () => {
    if (allTabsEmpty) {
      return EMPTY_STATE_COPY.myPatients.allEmpty;
    }

    if (isTodayTab) {
      return {
        ...EMPTY_STATE_COPY.myPatients.todayTab,
        onCtaClick: () => setActiveTab(PATIENT_VIEWS.active),
      };
    }

    return {
      ...EMPTY_STATE_COPY.myPatients.filteredTab,
      onCtaClick: () => setActiveTab(PATIENT_VIEWS.active),
    };
  };

  const renderPatientList = () => {
    if (isLoading) return <MyPatientsSkeleton />;

    if (isError) {
      return (
        <DashboardCardErrorState
          variant="stacked"
          onReload={onReload}
          isReloading={isReloading}
        />
      );
    }

    if (patients.length === 0) {
      const emptyConfig = getEmptyStateConfig();
      return <DashboardCardEmptyState {...emptyConfig} />;
    }

    if (isTodayTab) {
      return (
        <TodaysSessionsList
          appointments={patients}
          canJoin={canJoin}
          onJoin={onJoin}
          onOpenNote={onOpenNote}
        />
      );
    }

    return (
      <PatientList
        patients={patients}
        preserveOrder={activeTab !== PATIENT_VIEWS.active}
        tableName={tableName}
        tabKey={activeTab}
        rowComponent={
          activeTab === PATIENT_VIEWS.upcomingDischarge
            ? DischargePatientRow
            : PatientRow
        }
      />
    );
  };

  return (
    <ChakraBox
      data-testid="provider-dashboard-my-patients-card"
      {...CARD_STYLES}
      padding="22px 22px 10px"
    >
      <ChakraFlex alignItems="center" gap="10px" marginBottom="16px">
        <Text variant="h5">My patients</Text>
      </ChakraFlex>

      <PatientTabGrid
        activeTab={activeTab}
        onTabClick={handleTabClick}
        getTabCount={getTabCount}
      />

      <ChakraFlex
        data-testid="my-patients-content-area"
        direction="column"
        minHeight={CARD_CONTENT_MIN_HEIGHT.myPatients}
      >
        {renderPatientList()}
      </ChakraFlex>
    </ChakraBox>
  );
};

export default ProviderDashboardMyPatientsCard;
